import {
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { filter, Subscription } from 'rxjs';

import { LanguageServiceService } from '../../services/language-service.service';
import { LoginService } from '../../services/login.service';

type HeaderDropdown = 'candidates' | 'employers' | 'about' | 'profile';
type UserType = 'candidate' | 'admin' | 'company' | null;
type AuthState =
  | 'checking'
  | 'guest'
  | 'authenticated'
  | 'candidate'
  | 'company'
  | 'admin';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private subscriptions = new Subscription();

  private headerBootstrapped = false;

  headerLogicLoading = true;
  authReady = false;

  authState: AuthState = 'checking';

  loggedIn = false;
  userType: UserType = null;

  currentLanguage = 'en';
  currentUrl = '';

  mobileMenuOpen = false;
  activeDropdown: HeaderDropdown | null = null;

  searchPopupOpen = false;
  searchTitle = '';

  constructor(
    private languageService: LanguageServiceService,
    private transloco: TranslocoService,
    private router: Router,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.currentUrl = this.router.url;
    this.transloco.setActiveLang(this.currentLanguage);
  }

  ngOnInit(): void {
    this.bootstrapHeaderLogic();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.isBrowser) {
      window.removeEventListener('storage', this.onStorageChange);
      window.removeEventListener('auth-changed', this.onAuthChanged as EventListener);
    }
  }

  private bootstrapHeaderLogic(): void {
    if (this.headerBootstrapped) {
      return;
    }

    this.headerBootstrapped = true;

    if (!this.isBrowser) {
      return;
    }

    this.loadHeaderLogic();

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          )
        )
        .subscribe((event) => {
          this.currentUrl = event.urlAfterRedirects;

          this.refreshAuthState();

          this.closeMobileMenu();
          this.closeSearchPopup();
        })
    );

    let firstLanguageEmission = true;

    this.subscriptions.add(
      this.languageService.currentLanguage$.subscribe((lang) => {
        if (firstLanguageEmission) {
          firstLanguageEmission = false;
          return;
        }

        if (!lang || lang === this.currentLanguage) {
          return;
        }

        this.currentLanguage = lang;
        this.transloco.setActiveLang(lang);
        window.localStorage.setItem('language', lang);
      })
    );

    this.subscriptions.add(
      this.loginService.token$.subscribe(() => {
        this.refreshAuthState();
      })
    );

    window.addEventListener('storage', this.onStorageChange);
    window.addEventListener('auth-changed', this.onAuthChanged as EventListener);
  }

  private loadHeaderLogic(): void {
    const savedLanguage = window.localStorage.getItem('language') || 'en';

    this.currentLanguage = savedLanguage;
    this.transloco.setActiveLang(savedLanguage);

    this.currentUrl = this.router.url;

    this.authReady = false;
    this.headerLogicLoading = true;
    this.authState = 'checking';

    this.refreshAuthState();

    Promise.resolve().then(() => {
      this.authReady = true;
      this.headerLogicLoading = false;
      this.cdr.detectChanges();
    });
  }

  private onStorageChange = (): void => {
    this.ngZone.run(() => {
      this.refreshAuthState();
    });
  };

  private onAuthChanged = (): void => {
    this.ngZone.run(() => {
      this.refreshAuthState();
    });
  };

  private refreshAuthState(): void {
    if (!this.isBrowser) {
      return;
    }

    const token = this.getStoredToken();

    // أهم تعديل:
    // وجود token يعني المستخدم logged in حتى لو user_type لسه مش متخزن أو اسمه مختلف.
    // كده Login مستحيل يظهر مع وجود Profile.
    if (!token) {
      this.setGuestState();
      this.cdr.detectChanges();
      return;
    }

    const storedUserType = this.getStoredUserType();

    this.loggedIn = true;
    this.userType = storedUserType;
    this.authState = storedUserType || 'authenticated';

    this.cdr.detectChanges();
  }

  private setGuestState(): void {
    this.loggedIn = false;
    this.userType = null;
    this.authState = 'guest';
    this.activeDropdown = null;
  }

  private getStoredToken(): string | null {
    if (!this.isBrowser) {
      return null;
    }

    const possibleToken =
      this.loginService.getToken() ||
      window.localStorage.getItem('token') ||
      window.localStorage.getItem('access_token') ||
      window.localStorage.getItem('auth_token') ||
      window.localStorage.getItem('user_token') ||
      window.localStorage.getItem('bearer_token') ||
      window.localStorage.getItem('Authorization') ||
      window.localStorage.getItem('authorization');

    if (!possibleToken) {
      return null;
    }

    const token = String(possibleToken)
      .replace(/^Bearer\s+/i, '')
      .trim();

    if (
      !token ||
      token === 'null' ||
      token === 'undefined' ||
      token === '[object Object]'
    ) {
      return null;
    }

    return token;
  }

  private getStoredUserType(): UserType {
    if (!this.isBrowser) {
      return null;
    }

    const directType =
      window.localStorage.getItem('user_type') ||
      window.localStorage.getItem('account_type') ||
      window.localStorage.getItem('role') ||
      window.localStorage.getItem('user_role') ||
      window.localStorage.getItem('type');

    const normalizedDirectType = this.normalizeUserType(directType);

    if (normalizedDirectType) {
      return normalizedDirectType;
    }

    const storedUser =
      window.localStorage.getItem('user') ||
      window.localStorage.getItem('currentUser') ||
      window.localStorage.getItem('auth_user') ||
      window.localStorage.getItem('userData') ||
      window.localStorage.getItem('user_data');

    if (!storedUser) {
      return null;
    }

    try {
      const user = JSON.parse(storedUser);

      const possibleUserType =
        user?.user_type ||
        user?.account_type ||
        user?.role ||
        user?.user_role ||
        user?.type ||
        user?.data?.user_type ||
        user?.data?.account_type ||
        user?.data?.role ||
        user?.data?.user_role ||
        user?.data?.type ||
        user?.user?.user_type ||
        user?.user?.account_type ||
        user?.user?.role ||
        user?.user?.user_role ||
        user?.user?.type;

      return this.normalizeUserType(possibleUserType);
    } catch {
      return null;
    }
  }

  private normalizeUserType(value: any): UserType {
    const type = String(value || '').toLowerCase().trim();

    if (
      type === 'candidate' ||
      type === 'candidates' ||
      type === 'jobseeker' ||
      type === 'job_seeker' ||
      type === 'employee' ||
      type === '9'
    ) {
      return 'candidate';
    }

    if (
      type === 'company' ||
      type === 'companies' ||
      type === 'employer' ||
      type === 'employers' ||
      type === 'business'
    ) {
      return 'company';
    }

    if (type === 'admin' || type === 'super_admin' || type === '1') {
      return 'admin';
    }

    return null;
  }

  get showLoginButton(): boolean {
    return this.authReady && !this.loggedIn;
  }

  get showProfileButton(): boolean {
    return this.authReady && this.loggedIn;
  }

  isGuest(): boolean {
    return this.showLoginButton;
  }

  isLoggedIn(): boolean {
    return this.showProfileButton;
  }

  isCandidate(): boolean {
    return this.authReady && this.loggedIn && this.userType === 'candidate';
  }

  isCompany(): boolean {
    return this.authReady && this.loggedIn && this.userType === 'company';
  }

  isAdmin(): boolean {
    return this.authReady && this.loggedIn && this.userType === 'admin';
  }

  showCandidatesMenu(): boolean {
    if (!this.authReady) {
      return false;
    }

    return !this.loggedIn || this.userType !== 'company';
  }

  showEmployersMenu(): boolean {
    if (!this.authReady) {
      return false;
    }

    return !this.loggedIn || (this.userType !== 'candidate' && this.userType !== 'company');
  }

  get applyButtonRoute(): string {
    return this.isCompany() ? '/create-job' : '/jobs';
  }

  get profileRoute(): string {
    return this.isCompany() ? '/companyprofile' : '/profile';
  }

  get applyButtonLabelKey(): string {
    return this.isCompany() ? 'header.addjob' : 'header.applyNow';
  }

  logout(): void {
    if (!this.isBrowser) {
      return;
    }

    const savedLanguage = window.localStorage.getItem('language');

    window.localStorage.clear();

    if (savedLanguage) {
      window.localStorage.setItem('language', savedLanguage);
    }

    this.setGuestState();
    this.authReady = true;
    this.headerLogicLoading = false;

    this.closeMobileMenu();
    this.closeSearchPopup();

    // لو في أي component تاني سامع auth change
    window.dispatchEvent(new Event('auth-changed'));

    this.router.navigate(['/Home']);
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.searchPopupOpen) {
      this.closeSearchPopup();
    }

    if (this.activeDropdown) {
      this.activeDropdown = null;
    }
  }

  toggleMobileMenu(): void {
    if (this.headerLogicLoading || !this.authReady) {
      return;
    }

    this.mobileMenuOpen = !this.mobileMenuOpen;

    if (!this.mobileMenuOpen) {
      this.activeDropdown = null;
    }
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.activeDropdown = null;
  }

  toggleDropdown(dropdown: HeaderDropdown, event?: Event): void {
    if (this.headerLogicLoading || !this.authReady) {
      return;
    }

    event?.stopPropagation();

    this.activeDropdown = this.activeDropdown === dropdown ? null : dropdown;
  }

  toggleLanguage(): void {
    if (this.headerLogicLoading) {
      return;
    }

    const newLang = this.currentLanguage === 'en' ? 'ar' : 'en';

    this.currentLanguage = newLang;
    this.transloco.setActiveLang(newLang);

    if (this.isBrowser) {
      window.localStorage.setItem('language', newLang);
    }

    this.languageService.changeLanguage(newLang);
  }

  openSearchPopup(event?: Event): void {
    if (this.headerLogicLoading || !this.authReady) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();

    this.searchPopupOpen = true;
    this.activeDropdown = null;
    this.mobileMenuOpen = false;
  }

  closeSearchPopup(): void {
    this.searchPopupOpen = false;
    this.searchTitle = '';
  }

  onSearchInput(event: Event): void {
    this.searchTitle = (event.target as HTMLInputElement).value;
  }

  async submitSearch(event?: Event): Promise<void> {
    event?.preventDefault();

    const title = this.searchTitle.trim();

    if (!title) {
      return;
    }

    this.searchPopupOpen = false;
    this.searchTitle = '';

    await this.router.navigate(['/jobs'], {
      queryParams: {
        search: title,
      },
    });
  }
}
